import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authApi, type InvitationInfo } from "../api/auth";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Select } from "../components/Select";
import { SUPPORTED_LOCALES } from "../i18n";

const LOCALE_LABELS: Record<string, string> = {
  sr: "Srpski", en: "English", de: "Deutsch", hr: "Hrvatski", sl: "Slovenščina",
  it: "Italiano", es: "Español", fr: "Français", ru: "Русский", tr: "Türkçe",
};
const LOCALE_OPTIONS = SUPPORTED_LOCALES.map((l) => ({ value: l as string, label: LOCALE_LABELS[l] ?? l }));

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = searchParams.get("token") ?? "";

  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [locale, setLocale] = useState(navigator.language?.split("-")[0] || "en");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoadError({ status: 400, code: "MISSING_TOKEN", message: "No token in URL" });
      return;
    }
    authApi
      .inspectInvitation(token)
      .then((r) => setInfo(r))
      .catch((e: ApiError) => setLoadError(e));
  }, [token]);

  if (loadError) {
    return (
      <CenteredCard>
        <h1 style={titleStyle}>Invitation unavailable</h1>
        <p>
          {loadError.code === "INVITATION_EXPIRED"
            ? "This invitation has expired. Please contact whoever invited you for a new link."
            : loadError.code === "INVITATION_USED"
              ? "This invitation has already been accepted. Try signing in instead."
              : "We couldn't open this invitation. Please check the link."}
        </p>
        <Link to="/login" style={linkStyle}>
          Go to sign in
        </Link>
      </CenteredCard>
    );
  }

  if (!info) {
    return (
      <CenteredCard>
        <p>Loading invitation…</p>
      </CenteredCard>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (password !== confirm) {
      setSubmitError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await authApi.acceptInvitation(token, {
        firstName,
        lastName,
        password,
        locale,
        phone: phone.trim() || undefined,
      });
      await refresh();
      // Land on the profile with the welcome flag so the user is prompted to
      // upload an avatar before heading into the app proper.
      navigate("/profile?welcome=1", { replace: true });
    } catch (err) {
      const apiErr = err as ApiError;
      setSubmitError(translatePasswordError(apiErr));
    } finally {
      setSubmitting(false);
    }
  }

  const passwordRules = checkPasswordClient(password);

  return (
    <CenteredCard>
      <h1 style={titleStyle}>Welcome to {info.companyName ?? "Bankom Portal"}</h1>
      <p style={{ color: "var(--ink-3)", fontSize: 14 }}>
        You&apos;re joining as <strong>{info.email}</strong>
        {info.roles.length > 0 ? <> — role: {info.roles.join(", ")}</> : null}.
      </p>

      {submitError && <div style={errorStyle}>{submitError}</div>}

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", gap: 12 }}>
          <label style={{ ...labelStyle, flex: 1 }}>
            <span>First name</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required style={inputStyle} />
          </label>
          <label style={{ ...labelStyle, flex: 1 }}>
            <span>Last name</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} required style={inputStyle} />
          </label>
        </div>

        <label style={labelStyle}>
          <span>Phone <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>(optional)</span></span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+381 11 123 4567"
            autoComplete="tel"
            maxLength={40}
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            style={inputStyle}
          />
          <ul style={hintListStyle}>
            <Rule ok={passwordRules.minLength}>At least 12 characters</Rule>
            <Rule ok={passwordRules.hasUpper}>One uppercase letter (A–Z)</Rule>
            <Rule ok={passwordRules.hasLower}>One lowercase letter (a–z)</Rule>
            <Rule ok={passwordRules.hasDigit}>One digit (0–9)</Rule>
            <li style={{ color: "var(--ink-3)" }}>
              Cannot be a commonly leaked password (we check against a public breach list).
            </li>
          </ul>
        </label>
        <label style={labelStyle}>
          <span>Confirm password</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          <span>Language</span>
          <Select<string>
            value={locale}
            onChange={setLocale}
            options={LOCALE_OPTIONS}
          />
        </label>

        <button type="submit" disabled={submitting} style={btnStyle}>
          {submitting ? "Creating account…" : "Create account"}
        </button>
      </form>
    </CenteredCard>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return <div className="auth-shell" style={shellStyle}><div style={cardStyle}>{children}</div></div>;
}

function Rule({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li style={{ color: ok ? "var(--ok)" : "var(--ink-3)", listStyle: "none", display: "flex", gap: 6 }}>
      <span aria-hidden>{ok ? "✓" : "○"}</span>
      <span>{children}</span>
    </li>
  );
}

function checkPasswordClient(p: string) {
  return {
    minLength: p.length >= 12,
    hasUpper: /[A-Z]/.test(p),
    hasLower: /[a-z]/.test(p),
    hasDigit: /\d/.test(p),
  };
}

function translatePasswordError(err: ApiError): string {
  switch (err.code) {
    case "PASSWORD_TOO_SHORT":
      return "Password must be at least 12 characters.";
    case "PASSWORD_NEEDS_UPPERCASE":
      return "Password must contain at least one uppercase letter (A–Z).";
    case "PASSWORD_NEEDS_LOWERCASE":
      return "Password must contain at least one lowercase letter (a–z).";
    case "PASSWORD_NEEDS_DIGIT":
      return "Password must contain at least one digit (0–9).";
    case "PASSWORD_BREACHED":
      return "This password appears in a public breach corpus. Pick a different one.";
    case "INVITATION_EXPIRED":
      return "This invitation has expired. Ask the inviter to send a new one.";
    case "INVITATION_INVALID":
      return "Invitation is invalid. The link may have been used or revoked.";
    default:
      return err.message || "Could not accept the invitation.";
  }
}

const shellStyle: React.CSSProperties = {
  // Divided by the text-size zoom, which also scales viewport units inside .auth-shell.
  minHeight: "calc(100vh / var(--text-zoom))",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--surface-2)",
  padding: 16,
};
const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 480,
  background: "var(--surface)",
  padding: 32,
  borderRadius: 12,
  boxShadow: "var(--shadow-1)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const titleStyle: React.CSSProperties = { margin: 0, color: "var(--brand-ink)", fontSize: 24 };
const labelStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 6, fontSize: 14 };
const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 6,
  border: "1px solid var(--border-strong)",
  fontSize: 15,
};
const btnStyle: React.CSSProperties = {
  padding: "12px 16px",
  background: "var(--brand-primary-600)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};
const linkStyle: React.CSSProperties = { color: "var(--brand-ink)", fontSize: 14 };
const errorStyle: React.CSSProperties = {
  padding: 12,
  background: "var(--err-bg)",
  color: "var(--err)",
  borderRadius: 6,
  fontSize: 14,
};
const hintListStyle: React.CSSProperties = {
  margin: "8px 0 0",
  padding: 0,
  fontSize: 12,
  display: "flex",
  flexDirection: "column",
  gap: 3,
};
